import type { PresetDefinition } from '@/types';

/**
 * Quantities are in each service's catalog unit.
 *
 * When the pricing workbook moved a service to the unit US trades quote in, its
 * starter quantity was re-expressed rather than left to silently change meaning.
 * A length or an area keeps its size, rounded to a whole unit: 20 m of gutter is
 * 66 linear ft, 12 m of hedge 39 linear ft, 40 sq m of carpet 431 sq ft. A service
 * now sold per visit or per application counts one round of work, not the square
 * metres it used to measure.
 */
const carDetailing: readonly PresetDefinition[] = [
  { id: 'car-detailing.express', name: 'Express Wash', description: 'The 30-minute turnaround', icon: 'droplets', color: 'indigo', displayOrder: 10,
    items: [{ serviceId: 'car-detailing.exterior-wash', quantity: 1 }, { serviceId: 'car-detailing.tyre-dressing', quantity: 1 }] },
  { id: 'car-detailing.full-detail', name: 'Full Detail', description: 'Inside and out, wax finish', icon: 'sparkles', color: 'violet', displayOrder: 20,
    items: [
      { serviceId: 'car-detailing.exterior-wash', quantity: 1 },
      { serviceId: 'car-detailing.interior-cleaning', quantity: 1 },
      { serviceId: 'car-detailing.wax-polish', quantity: 1 },
      { serviceId: 'car-detailing.tyre-dressing', quantity: 1 },
    ] },
  { id: 'car-detailing.ceramic-package', name: 'Ceramic Package', description: 'Correction plus 9H coating', icon: 'gem', color: 'amber', displayOrder: 30,
    items: [
      { serviceId: 'car-detailing.foam-wash', quantity: 1 },
      { serviceId: 'car-detailing.paint-correction', quantity: 1 },
      { serviceId: 'car-detailing.ceramic-coating', quantity: 1 },
    ] },
];

const pressureWashing: readonly PresetDefinition[] = [
  { id: 'pressure-washing.drive-and-patio', name: 'Drive & Patio', icon: 'waves', color: 'blue', displayOrder: 10,
    items: [{ serviceId: 'pressure-washing.driveway-wash', quantity: 40 }, { serviceId: 'pressure-washing.patio-wash', quantity: 25 }] },
  { id: 'pressure-washing.full-exterior', name: 'Full Exterior', description: 'House, drive and gutters', icon: 'home', color: 'sky', displayOrder: 20,
    items: [
      { serviceId: 'pressure-washing.house-wash', quantity: 120 },
      { serviceId: 'pressure-washing.driveway-wash', quantity: 40 },
      { serviceId: 'pressure-washing.gutter-cleaning', quantity: 66 },
    ] },
  { id: 'pressure-washing.seal-and-protect', name: 'Seal & Protect', icon: 'shield', color: 'emerald', displayOrder: 30,
    items: [{ serviceId: 'pressure-washing.concrete-cleaning', quantity: 40 }, { serviceId: 'pressure-washing.concrete-sealing', quantity: 40 }] },
];

const plumbing: readonly PresetDefinition[] = [
  { id: 'plumbing.emergency-callout', name: 'Emergency Call-out', icon: 'siren', color: 'rose', displayOrder: 10,
    items: [{ serviceId: 'plumbing.emergency-visit', quantity: 1 }, { serviceId: 'plumbing.leak-repair', quantity: 1 }] },
  { id: 'plumbing.bathroom-refit', name: 'Bathroom Refit', description: 'Suite, toilet and taps', icon: 'bath', color: 'sky', displayOrder: 20,
    items: [
      { serviceId: 'plumbing.bathroom-fitting', quantity: 1 },
      { serviceId: 'plumbing.toilet-installation', quantity: 1 },
      { serviceId: 'plumbing.tap-installation', quantity: 2 },
    ] },
];

const electrician: readonly PresetDefinition[] = [
  { id: 'electrician.room-fitout', name: 'Room Fit-out', description: 'Lights, switches and sockets', icon: 'lightbulb', color: 'amber', displayOrder: 10,
    items: [
      { serviceId: 'electrician.light-installation', quantity: 4 },
      { serviceId: 'electrician.switch-installation', quantity: 2 },
      { serviceId: 'electrician.socket-installation', quantity: 4 },
    ] },
  { id: 'electrician.safety-check', name: 'Safety Check', icon: 'badge-check', color: 'blue', displayOrder: 20,
    items: [{ serviceId: 'electrician.electrical-inspection', quantity: 1 }, { serviceId: 'electrician.mcb-replacement', quantity: 1 }] },
];

const hvac: readonly PresetDefinition[] = [
  { id: 'hvac.annual-service', name: 'Annual Service', description: 'Per unit, filters and coils', icon: 'fan', color: 'cyan', displayOrder: 10,
    items: [
      { serviceId: 'hvac.general-service', quantity: 1 },
      { serviceId: 'hvac.coil-cleaning', quantity: 1 },
      { serviceId: 'hvac.inspection', quantity: 1 },
    ] },
  { id: 'hvac.new-install', name: 'New Unit Install', icon: 'snowflake', color: 'sky', displayOrder: 20,
    items: [{ serviceId: 'hvac.installation', quantity: 1 }, { serviceId: 'hvac.gas-refill', quantity: 1 }] },
];

const lawnCare: readonly PresetDefinition[] = [
  { id: 'lawn-care.monthly-round', name: 'Monthly Round', icon: 'leaf', color: 'green', displayOrder: 10,
    items: [{ serviceId: 'lawn-care.lawn-mowing', quantity: 1 }, { serviceId: 'lawn-care.weed-removal', quantity: 1 }] },
  { id: 'lawn-care.spring-tidy', name: 'Spring Tidy', description: 'Clearance, weeds and hedges', icon: 'sprout', color: 'lime', displayOrder: 20,
    items: [
      { serviceId: 'lawn-care.garden-cleaning', quantity: 3 },
      { serviceId: 'lawn-care.weed-removal', quantity: 1 },
      { serviceId: 'lawn-care.hedge-trimming', quantity: 39 },
    ] },
];

const homeCleaning: readonly PresetDefinition[] = [
  { id: 'home-cleaning.standard', name: 'Standard Clean', description: 'Kitchen, bathrooms, bedrooms', icon: 'home', color: 'violet', displayOrder: 10,
    items: [
      { serviceId: 'home-cleaning.kitchen-cleaning', quantity: 1 },
      { serviceId: 'home-cleaning.bathroom-cleaning', quantity: 2 },
      { serviceId: 'home-cleaning.bedroom-cleaning', quantity: 3 },
    ] },
  { id: 'home-cleaning.end-of-tenancy', name: 'End of Tenancy', icon: 'truck', color: 'blue', displayOrder: 20,
    items: [
      { serviceId: 'home-cleaning.deep-cleaning', quantity: 1 },
      { serviceId: 'home-cleaning.carpet-cleaning', quantity: 431 },
      { serviceId: 'home-cleaning.window-cleaning', quantity: 6 },
    ] },
];

const salonSpa: readonly PresetDefinition[] = [
  { id: 'salon-spa.cut-and-colour', name: 'Cut & Colour', icon: 'wand', color: 'purple', displayOrder: 10,
    items: [
      { serviceId: 'salon-spa.hair-wash', quantity: 1 },
      { serviceId: 'salon-spa.hair-cut', quantity: 1 },
      { serviceId: 'salon-spa.hair-colour', quantity: 1 },
    ] },
  { id: 'salon-spa.grooming', name: 'Grooming Package', icon: 'feather', color: 'slate', displayOrder: 20,
    items: [
      { serviceId: 'salon-spa.hair-cut', quantity: 1 },
      { serviceId: 'salon-spa.beard-trim', quantity: 1 },
      { serviceId: 'salon-spa.shaving', quantity: 1 },
    ] },
];

/** Starter bundles per vertical. Owners add their own from any quote. */
export const PRESET_SETS: Readonly<Record<string, readonly PresetDefinition[]>> = {
  'car-detailing': carDetailing,
  'pressure-washing': pressureWashing,
  plumbing,
  electrician,
  hvac,
  'lawn-care': lawnCare,
  'home-cleaning': homeCleaning,
  'salon-spa': salonSpa,
  other: [],
};

export function getPresets(templateId: string): readonly PresetDefinition[] {
  return PRESET_SETS[templateId] ?? [];
}
